import React, {FC, useState} from 'react';
import {LinearProgress, TextField} from "@mui/material";
import Autocomplete from '@mui/material/Autocomplete';
import TagService from "../services/tag-service";
import FlexStack from "../components/Common/flex-stack";
import {useHistory, useParams} from "react-router-dom";
import RouteService from "../services/route-service";
import {useAnimal} from "../components/Common/hooks/use-animal";
import {useQuery} from "@tanstack/react-query";
import {FormActions, Widget} from "../components/Common";
import SellIcon from "@mui/icons-material/Sell";

const useTags = () => {
    const queryInfo = useQuery({
        queryKey: ['tags'],
        queryFn: () => TagService.loadTags(),
    });

    return {
        ...queryInfo,
        tags: queryInfo.data ?? [],
    };
};

interface TagEditorPathParams {
    panonId: string;
}

const TagAutocomplete = ({animalId, animalName, tags, onClose}: {
    animalId: string,
    animalName: string,
    tags: string[],
    onClose?: () => void,
}) => {
    const [tagNames, setTagNames] = useState(tags);
    const {tags: availableTags} = useTags();

    const handleSaveClicked = async () => {
        await TagService.setTags({animalId, tagNames});
        onClose?.();
    };

    return (
        <FlexStack>
            <Widget displayName={<>
                <SellIcon fontSize="small" sx={{mr: 1}}/>
                Tags bearbeiten: {animalName}
            </>}>
                <FlexStack>
                    <Autocomplete
                        className="tags-input"
                        options={availableTags}
                        multiple
                        freeSolo
                        renderInput={(params) => <TextField {...params} label="Tags" variant="outlined"/>}
                        value={tagNames}
                        onChange={(event, newValue) => setTagNames(newValue)}
                        autoSelect
                        autoHighlight
                    />
                    <FormActions
                        onCancel={onClose}
                        onSave={handleSaveClicked}
                    />
                </FlexStack>
            </Widget>
        </FlexStack>
    );
};

const TagEditor: FC = () => {
    const history = useHistory();
    const {panonId} = useParams<TagEditorPathParams>();
    const {animal, isLoading} = useAnimal(panonId);

    if (isLoading) {
        return <LinearProgress/>
    }

    if (!animal) {
        return null;
    }

    const handleClose = () => history.push(RouteService.expand(RouteService.ANIMAL_DETAILS, {panonId}));

    return <TagAutocomplete animalId={animal.id} animalName={animal.name} tags={animal.tags} onClose={handleClose}/>;
}

export default TagEditor;
